import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';

import { useSortAndFilterTreatmentsContext } from '../context/SortAndFilterTreatmentsContext';
import { useTreatmentFilterStateContext } from '../TreatmentFilter/context/TreatmentFilterContext';
import { useFilterAndSortTreatments } from '../../../../hooks/useFilterAndSortTreatments';
import { useTreatmentSortStateContext } from '../TreatmentSort/context/TreatmentSortContext';
import { ZeroResultsCommand } from '../../../../modules/sortAndFilter/patterns/commands/googleAnalytics';
import { gaEvents } from '../../../../modules/analytics/google/dispatchGoogleAnalyticsEvent';
import { RedisTreatment } from '../../../../lib/redis/types/RedisTreatment';
import { useSortAndFilterConfigContext } from '../context/SortAndFilterConfigContext';
import { sortCommands } from '../common/SortCommands';

interface Props {
    children: React.ReactNode;
}

interface Results extends Omit<ReturnType<typeof useFilterAndSortTreatments>, 'processData'> {
    processedData: RedisTreatment[] | null;
}

const SortAndFilterResultsContext = createContext<Results>({} as Results);

const SortAndFilterResultsProvider = ({ children }: Props) => {
    const treatments = useSortAndFilterTreatmentsContext();
    const { filters, numberOfFiltersApplied } = useTreatmentFilterStateContext();
    const sortMethod = useTreatmentSortStateContext();
    const config = useSortAndFilterConfigContext();
    const zeroResultCommand = useRef<ZeroResultsCommand>(new ZeroResultsCommand());

    const filterTypes = useMemo(() => {
        const filterValues = Array.from(filters.values());
        const filterTypesFromFilters = filterValues.map((filter) => filter.filterTypes());
        return filterTypesFromFilters.reduce((acc, filterTypes) => acc.concat(filterTypes), []);
    }, [filters]);

    const results = useFilterAndSortTreatments({
        filterTypes,
        sortTypes: [sortCommands[sortMethod]],
        data: treatments,
        config,
    }) as Results;

    useEffect(() => {
        const filteredTreatments = results.processedData || [];

        if (numberOfFiltersApplied > 0 && filteredTreatments.length === 0) {
            zeroResultCommand.current.execute({
                event: gaEvents.ZeroResults,
                list: window.location.pathname,
                filters,
            });
        }
    }, [numberOfFiltersApplied, results]);

    return <SortAndFilterResultsContext.Provider value={results}>{children}</SortAndFilterResultsContext.Provider>;
};

export const useSortAndFilterResultsContext = () => {
    const results = useContext(SortAndFilterResultsContext);

    if (results === undefined) {
        throw new Error('useSortAndFilterResultsContext must be used within a SortAndFilterResultsProvider.');
    }

    return results;
};

export default SortAndFilterResultsProvider;
